import React, { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  DataGrid,
  GridColDef,
  GridRowModesModel,
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
} from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import Button from "../mui/Button";
import Dialog from "../common/Dialog";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Loader from "../common/Loader";

let deleteId: number | null = null;
let editId: number | null = null;
let saveId: number | null = null;

interface TableDialogProps {
  open: any;
  onClose: () => void;
  headers: any;
  sheetData: { id: number; [key: string]: any }[];
  onSubmit: (data: any) => void;
  onEdit: (id: number, updatedData: any) => void;
  onDelete: (id: number) => void;
  uploading: boolean;
  isSaveButtonEnabled: any;
  errorMessages: any;
  isLoading: boolean;
  setSheetData: any;
}

const TargetTable: React.FC<TableDialogProps> = ({
  open,
  onClose,
  headers,
  sheetData = [],
  onSubmit,
  onEdit,
  onDelete,
  uploading,
  isSaveButtonEnabled,
  errorMessages,
  isLoading,
  setSheetData,
}) => {
  const [tableData, setTableData] = useState(sheetData);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [isDeleteDisabled, setIsDeleteDisabled] = useState<boolean>(false);

  useEffect(() => {
    setTableData(sheetData);
    setSheetData(sheetData);
  }, [sheetData]);

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === "rowFocusOut") {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: any) => {
    if (id && id == editId) {
      return;
    }
    editId = id;
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleSaveClick = (id: any) => {
    if (id && id == saveId) {
      return;
    }
    saveId = id;
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View },
    }));
    const updatedRow = tableData.find((row: any) => row.id === id);

    if (updatedRow) {
      onEdit(id as number, updatedRow);
    }
  };

  const handleDeleteClick = (id: any) => {
    if (id && id == deleteId) {
      return;
    }
    deleteId = id;
    if (!tableData.some((row: any) => row?.id === id)) {
      return;
    }

    setIsDeleteDisabled(true);
    // Remove the row from tableData and sheetData
    setTableData((prev: any[]) => prev?.filter((row: any) => row?.id !== id));
    setSheetData((prev: any[]) => prev?.filter((row: any) => row?.id !== id));

    setIsDeleteDisabled(false);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel((prev: any) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  // ...existing code...

  const processRowUpdate = (newRow: any, oldRow: any) => {
    try {
      const updatedRow = { ...oldRow, ...newRow };
      const rowExists = tableData.some((row) => row.id === updatedRow.id);

      if (!rowExists) {
        console.error(`No row with id #${updatedRow.id} found`);
        return oldRow; // Return the old row to prevent the update
      }

      // Update the tableData with the new row data
      setTableData((prevData) =>
        prevData.map((row) => (row.id === updatedRow.id ? updatedRow : row))
      );
      return updatedRow;
    } catch (error) {
      console.log(error);
    }
  };

  // ...existing code...
  const columns: GridColDef[] = [
    ...headers?.map((h: any) => ({
      field: h.keyName,
      headerName: h.label,
      width: h.width,
      editable: h.editable,
    })),
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 150,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const hasError = Boolean(errorMessages[id]);

        isSaveButtonEnabled(rowModesModel);

        const actions = isInEditMode
          ? [
              <GridActionsCellItem
                icon={<SaveIcon />}
                label="Save"
                onClick={() => {
                  setTimeout(() => {
                    handleSaveClick(id);
                  }, 100);
                }}
                sx={{
                  border: "1px solid #997906",
                  backgroundColor: "#FFF1B7",
                  "&:hover": {
                    backgroundColor: "#e8d78e",
                  },
                  padding: "5px 10px",
                  borderRadius: "8px",
                }}
              />,
              <GridActionsCellItem
                icon={<CloseIcon />}
                label="Cancel"
                onClick={handleCancelClick(id)}
                sx={{
                  border: "1px solid #997906",
                  backgroundColor: "#FFF1B7",
                  "&:hover": {
                    backgroundColor: "#e8d78e",
                  },
                  padding: "5px 10px",
                  borderRadius: "8px",
                }}
              />,
            ]
          : [
              <GridActionsCellItem
                icon={<ModeEditOutlineOutlinedIcon />}
                label="Edit"
                onClick={() => {
                  setTimeout(() => {
                    handleEditClick(id);
                  }, 100);
                }}
                sx={{
                  border: "1px solid #997906",
                  backgroundColor: "#FFF1B7",
                  "&:hover": {
                    backgroundColor: "#e8d78e",
                  },
                  padding: "5px 10px",
                  borderRadius: "8px",
                }}
              />,
              <GridActionsCellItem
                icon={<DeleteOutlineIcon />}
                label="Delete"
                disabled={isDeleteDisabled}
                onClick={(e) => {
                  setIsDeleteDisabled(true);
                  setTimeout(() => {
                    handleDeleteClick(id);
                  }, 100);
                }}
                sx={{
                  border: "1px solid #997906",
                  backgroundColor: "#FFF1B7",
                  "&:hover": {
                    backgroundColor: "#e8d78e",
                  },
                  padding: "5px 10px",
                  borderRadius: "8px",
                }}
              />,
            ];

        if (hasError) {
          actions.unshift(
            <Tooltip
              title={errorMessages[id].split("records[0].")[1] || "Error"}
              arrow
              placement="top"
            >
              <span>
                {" "}
                {/* Added wrapper element */}
                <GridActionsCellItem
                  icon={<ErrorOutlineIcon color="error" />}
                  label="Error"
                  disabled
                  sx={{
                    padding: "5px 10px",
                    borderRadius: "8px",
                    cursor: "help", // Changes cursor to indicate hoverable element
                  }}
                  onClick={(e) => e.preventDefault()} // Prevent any click actions
                />
              </span>
            </Tooltip>
          );
        }

        return actions;
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      maxWidth="lg"
      disabledBackdropClick={true}
    >
      <Box sx={{ padding: "24px", paddingTop: "12px" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: "16px" }}
        >
          <Typography variant="h6">Preview</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* {tableData.length > 0 && ( */}
        <div style={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={tableData}
            columns={columns}
            editMode="row"
            // pagination={false}
            // disableSelectionOnClick
            onRowEditStop={handleRowEditStop}
            showColumnVerticalBorder={true}
            showCellVerticalBorder={true}
            processRowUpdate={processRowUpdate}
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            sx={{
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#DADADA",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 550,
                fontSize: "14px",
              },
              "& .MuiDataGrid-row": {
                backgroundColor: "#FFFFFF",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#FFFFFF",
              },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
          />
        </div>
        {/* )} */}

        <Box
          display="flex"
          justifyContent="flex-end"
          sx={{ marginTop: "16px", gap: "12px" }}
        >
          <Button
            variant="outlined"
            sx={{
              color: "black",
              border: "1px solid #8D8D8D",
              borderRadius: "7px",
              textTransform: "capitalize",
            }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onSubmit(tableData)}
            disabled={uploading}
            loading={isLoading}
            sx={{
              border: "1px solid #a18218",
              backgroundColor: "#FFF1B7",
              "&:hover": {
                backgroundColor: "#e8d78e",
              },
              borderRadius: "8px",
              textTransform: "capitalize",
              boxShadow: "none !important",
            }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TargetTable;
